import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Save, Sparkles, Eye, CalendarClock, Send, Image as Img, Loader2, Share2, UploadCloud, Crop } from "lucide-react";
import { api } from "@/lib/api";
import { RichEditor } from "@/components/magazine/RichEditor";
import { MediaPicker } from "@/components/magazine/MediaPicker";
import { AIImageModal } from "@/components/magazine/AIImageModal";
import { MagCropModal } from "@/components/magazine/MagCropModal";
import { appPrompt } from "@/components/AppDialogHost";

const TEMPLATES = {
  standard: "<p>Start writing…</p>",
  music_news: "<h2>The Story</h2><p></p><h2>Why It Matters</h2><p></p>",
  artist_spotlight: "<h2>The Origin</h2><p></p><h2>The Sound</h2><p></p><blockquote>Pull quote here</blockquote><h2>What's Next</h2><p></p>",
  album_review: "<h2>First Listen</h2><p></p><h2>Standout Tracks</h2><ul><li></li></ul><h2>The Verdict</h2><p></p>",
  single_review: "<h2>The Track</h2><p></p><h2>The Verdict</h2><p></p>",
  interview: "<p><strong>Q: </strong></p><p>A: </p><p><strong>Q: </strong></p><p>A: </p>",
  top_list: "<h2>10. </h2><p></p><h2>9. </h2><p></p><h2>8. </h2><p></p>",
  throwback: "<h2>Back Then</h2><p></p><h2>The Legacy</h2><p></p><h2>Today</h2><p></p>",
  radio_feature: "<h2>On The Air</h2><p></p><h2>Tune In</h2><p></p>",
};

export default function MagEditor() {
  const { articleId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const isNew = articleId === "new";
  const [art, setArt] = useState(null);
  const [cats, setCats] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [picker, setPicker] = useState(null); // "featured" | callback fn
  const [aiImg, setAiImg] = useState(null); // "featured" | insert-callback fn
  const [cropSrc, setCropSrc] = useState(null); // {file} | {src}
  const [busy, setBusy] = useState("");
  const [pubSlug, setPubSlug] = useState("");
  const [social, setSocial] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/mag/categories").then((r) => setCats(r.data.categories || []));
    api.get("/mag/authors").then((r) => setAuthors(r.data.authors || []));
    api.get("/mag/publication").then((r) => setPubSlug(r.data.slug));
    api.get("/mag/social/options").then((r) => setSocial(r.data.platforms || [])).catch(() => {});
    if (isNew) {
      const template = params.get("template") || "standard";
      api.post("/mag/articles", { title: params.get("title") || "Untitled Article", body: TEMPLATES[template] || TEMPLATES.standard, template })
        .then((r) => { setArt(r.data); nav(`/dashboard/magazine/editor/${r.data.article_id}`, { replace: true }); });
    } else {
      api.get(`/mag/articles/${articleId}`).then((r) => setArt(r.data)).catch(() => toast.error("Article not found"));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleId]);

  if (!art) return <div className="p-8 text-sm text-ink-muted">Loading editor…</div>;

  const set = (k, v) => setArt((a) => ({ ...a, [k]: v }));
  const save = async (silent) => {
    setBusy("save");
    try {
      await api.put(`/mag/articles/${art.article_id}`, {
        title: art.title, subtitle: art.subtitle, body: art.body, featured_image: art.featured_image,
        author_id: art.author_id, category_ids: art.category_ids, tags: art.tags,
        is_featured: art.is_featured, feature_placement: art.feature_placement,
        seo_title: art.seo_title, meta_description: art.meta_description,
        og_title: art.og_title, og_description: art.og_description, og_image: art.og_image,
        social_caption: art.social_caption, slug: art.slug,
        share_platforms: art.share_platforms || [] });
      if (!silent) toast.success("Draft saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy("");
  };
  const doAction = async (action, extra = {}) => {
    await save(true);
    setBusy(action);
    try {
      const res = await api.post(`/mag/articles/${art.article_id}/action`, { action, ...extra });
      if (action === "publish") {
        const shared = res.data.shared || [];
        const skipped = res.data.share_skipped || [];
        toast.success(shared.length ? `Published! Sharing to ${shared.join(", ")}…` : "Published! It's live on the magazine.");
        if (skipped.length) toast.info(`Not shared to ${skipped.join(", ")} — not connected or missing image`);
      } else toast.success(`Article ${action}d`);
      const r = await api.get(`/mag/articles/${art.article_id}`);
      setArt(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy("");
  };
  const schedule = async () => {
    const when = await appPrompt("Publish date & time (YYYY-MM-DD HH:MM, UTC):");
    if (!when) return;
    doAction("schedule", { scheduled_at: new Date(when.replace(" ", "T") + "Z").toISOString() });
  };
  const genSeo = async () => {
    setBusy("seo");
    try {
      const r = await api.post("/mag/ai/seo", { title: art.title, body: art.body });
      setArt((a) => ({ ...a, seo_title: r.data.seo_title || a.seo_title, meta_description: r.data.meta_description || a.meta_description, og_title: r.data.og_title || "", og_description: r.data.og_description || "", social_caption: r.data.social_caption || a.social_caption, slug: r.data.slug || a.slug }));
      toast.success("SEO generated — review and save");
    } catch (e) { toast.error(e?.response?.data?.detail || "SEO generation failed"); }
    setBusy("");
  };
  const toggleCat = (id) => set("category_ids", art.category_ids.includes(id) ? art.category_ids.filter((c) => c !== id) : [...art.category_ids, id]);
  const toggleShare = (p) => set("share_platforms", (art.share_platforms || []).includes(p) ? (art.share_platforms || []).filter((x) => x !== p) : [...(art.share_platforms || []), p]);
  const connectedSocial = social.filter((s) => s.connected);

  return (
    <div className="space-y-4" data-testid="mag-editor-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase ${art.status === "published" ? "border-emerald-500/50 text-emerald-300" : "border-line text-ink-secondary"}`}>{art.status}</span>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => save(false)} disabled={!!busy} data-testid="mag-save-draft-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-1.5 text-xs font-bold">{busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save Draft</button>
          <button onClick={async () => { await save(true); window.open(`/mag/${pubSlug}/a/${art.slug}?preview=${art.article_id}`, "_blank"); }} data-testid="mag-preview-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-1.5 text-xs font-bold"><Eye className="h-3.5 w-3.5" /> Preview</button>
          <button onClick={schedule} disabled={!!busy} data-testid="mag-schedule-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/50 text-amber-300 px-4 py-1.5 text-xs font-bold"><CalendarClock className="h-3.5 w-3.5" /> Schedule</button>
          {art.status === "published" ? (
            <button onClick={() => doAction("unpublish")} disabled={!!busy} data-testid="mag-unpublish-btn"
              className="rounded-full border border-red-500/50 text-red-300 px-4 py-1.5 text-xs font-bold">Unpublish</button>
          ) : (
            <button onClick={() => doAction("publish")} disabled={!!busy} data-testid="mag-publish-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-amber text-black px-4 py-1.5 text-xs font-bold">{busy === "publish" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Publish</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <input value={art.title} onChange={(e) => set("title", e.target.value)} placeholder="Article title" data-testid="mag-title-input"
            className="w-full bg-transparent font-display text-3xl font-bold focus:outline-none border-b border-line pb-2" />
          <input value={art.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Subtitle / dek" data-testid="mag-subtitle-input"
            className="w-full bg-transparent text-lg text-ink-secondary focus:outline-none border-b border-line pb-2" />
          <RichEditor value={art.body} onChange={(v) => set("body", v)}
            onPickImage={(cb) => setPicker(() => cb)}
            onAIImage={(cb) => setAiImg(() => cb)} />
        </div>

        <div className="space-y-3">
          <div className="rounded-xl border border-line bg-bg-surface p-3 space-y-2">
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Featured Image</div>
            {art.featured_image ? <img src={art.featured_image} alt="" className="w-full h-32 object-cover rounded-md" /> : null}
            {art.featured_image ? (
              <button onClick={() => setCropSrc({ src: art.featured_image })} data-testid="mag-crop-featured-btn"
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-brand-cyan/50 text-brand-cyan px-3 py-2 text-xs font-bold">
                <Crop className="h-3.5 w-3.5" /> Crop / Reframe</button>
            ) : null}
            <button onClick={() => fileRef.current?.click()} data-testid="mag-upload-featured-btn"
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-line px-3 py-2 text-xs">
              <UploadCloud className="h-3.5 w-3.5" /> Upload from computer</button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="mag-featured-file-input"
              onChange={(e) => { if (e.target.files?.[0]) setCropSrc({ file: e.target.files[0] }); e.target.value = ""; }} />
            <button onClick={() => setPicker("featured")} data-testid="mag-pick-featured-btn"
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-line px-3 py-2 text-xs"><Img className="h-3.5 w-3.5" /> Choose from library</button>
            <button onClick={() => setAiImg("featured")} data-testid="mag-ai-featured-btn"
              className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-brand-amber/60 text-brand-amber px-3 py-2 text-xs font-bold"><Sparkles className="h-3.5 w-3.5" /> Generate with AI</button>
          </div>
          <div className="rounded-xl border border-line bg-bg-surface p-3">
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-2">Categories</div>
            <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
              {cats.map((c) => (
                <button key={c.category_id} onClick={() => toggleCat(c.category_id)} data-testid={`mag-cat-${c.slug}`}
                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold border ${art.category_ids.includes(c.category_id) ? "bg-brand-amber text-black border-brand-amber" : "border-line text-ink-secondary"}`}>{c.name}</button>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-line bg-bg-surface p-3 space-y-2">
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Tags (comma separated)</div>
            <input value={(art.tags || []).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} data-testid="mag-tags-input"
              className="w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs" />
            <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted pt-1">Author</div>
            <select value={art.author_id} onChange={(e) => set("author_id", e.target.value)} data-testid="mag-author-select"
              className="w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs">
              {authors.map((a) => <option key={a.author_id} value={a.author_id}>{a.name}</option>)}
            </select>
          </div>
          <div className="rounded-xl border border-line bg-bg-surface p-3 space-y-2">
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={!!art.is_featured} onChange={(e) => set("is_featured", e.target.checked)} data-testid="mag-featured-toggle" />
              Feature on Homepage
            </label>
            <select value={art.feature_placement} onChange={(e) => set("feature_placement", e.target.value)} data-testid="mag-placement-select"
              className="w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs">
              <option value="hero">Main Hero Story</option><option value="featured">Featured Story</option>
              <option value="category">Category Feature</option><option value="normal">Normal Article</option>
            </select>
          </div>
          <div className="rounded-xl border border-line bg-bg-surface p-3 space-y-2" data-testid="mag-share-card">
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-ink-muted">
              <Share2 className="h-3 w-3" /> Share on Publish
            </div>
            {connectedSocial.length === 0 ? (
              <div className="text-[11px] text-ink-muted" data-testid="mag-share-none">
                No social accounts connected. <a href="/dashboard/social-connections" className="text-brand-cyan underline">Connect accounts →</a>
              </div>
            ) : connectedSocial.map((s) => (
              <label key={s.platform} className="flex items-center gap-2 text-xs capitalize">
                <input type="checkbox" checked={(art.share_platforms || []).includes(s.platform)}
                  onChange={() => toggleShare(s.platform)} data-testid={`mag-share-${s.platform}`} />
                {s.platform}
                {s.account_label && <span className="text-[10px] text-ink-muted normal-case truncate">({s.account_label})</span>}
              </label>
            ))}
            {(art.share_platforms || []).length > 0 && (
              <div className="text-[10px] text-ink-muted" data-testid="mag-share-summary">
                Auto-posts to {(art.share_platforms || []).length} platform{(art.share_platforms || []).length > 1 ? "s" : ""} the moment this story goes live — including scheduled publishes.
              </div>
            )}
          </div>
          <div className="rounded-xl border border-line bg-bg-surface p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">SEO</div>
              <button onClick={genSeo} disabled={!!busy} data-testid="mag-gen-seo-btn"
                className="inline-flex items-center gap-1 rounded-full border border-brand-cyan/60 text-brand-cyan px-2 py-0.5 text-[10px] font-bold">
                {busy === "seo" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Generate SEO</button>
            </div>
            <input value={art.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} placeholder="SEO title" data-testid="mag-seo-title"
              className="w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs" />
            <textarea value={art.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} placeholder="Meta description" rows={2} data-testid="mag-meta-desc"
              className="w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs" />
            <input value={art.slug || ""} onChange={(e) => set("slug", e.target.value)} placeholder="url-slug" data-testid="mag-slug-input"
              className="w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs font-mono" />
            <textarea value={art.social_caption || ""} onChange={(e) => set("social_caption", e.target.value)} placeholder="Social share caption" rows={2} data-testid="mag-social-caption"
              className="w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs" />
          </div>
        </div>
      </div>

      <MediaPicker open={!!picker} onClose={() => setPicker(null)}
        onSelect={(m) => {
          if (picker === "featured") set("featured_image", m.file_url);
          else if (typeof picker === "function") picker(m.file_url, m.alt_text);
        }} />
      <AIImageModal open={!!aiImg} onClose={() => setAiImg(null)}
        onUse={(m) => {
          if (aiImg === "featured") set("featured_image", m.file_url);
          else if (typeof aiImg === "function") aiImg(m.file_url, m.alt_text);
        }}
        context={{ title: art.title, subtitle: art.subtitle, body: art.body }} />
      <MagCropModal open={!!cropSrc} source={cropSrc} onClose={() => setCropSrc(null)}
        onDone={(m) => { set("featured_image", m.file_url); toast.success("Featured image updated"); }} />
    </div>
  );
}
